import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import type { MeResponse, PublicAgent } from '@t3/shared';
import { apiFetch } from '../api/client.ts';

interface AuthState {
  agent: PublicAgent | null;
  remainingBackupCodes: number | null;
  loading: boolean;
  refresh: () => Promise<void>;
  setAgent: (agent: PublicAgent | null, remaining?: number) => void;
  logout: () => Promise<void>;
}

const AuthCtx = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }): React.ReactElement {
  const [agent, setAgentState] = useState<PublicAgent | null>(null);
  const [remainingBackupCodes, setRemaining] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const me = await apiFetch<MeResponse>('/api/auth/agent/me');
      setAgentState(me.agent);
      setRemaining(me.remainingBackupCodes ?? null);
    } catch {
      setAgentState(null);
      setRemaining(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const setAgent = useCallback((next: PublicAgent | null, remaining?: number) => {
    setAgentState(next);
    if (remaining !== undefined) setRemaining(remaining);
    setLoading(false);
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiFetch('/api/auth/agent/logout', { method: 'POST' });
    } finally {
      setAgentState(null);
      setRemaining(null);
    }
  }, []);

  const value = useMemo<AuthState>(
    () => ({ agent, remainingBackupCodes, loading, refresh, setAgent, logout }),
    [agent, remainingBackupCodes, loading, refresh, setAgent, logout],
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
